import { useCallback, useContext, useMemo, useReducer, createContext, ReactNode } from 'react';
import uniqid from 'uniqid';

import {
    ADD_TOAST,
    ADD_MULTIPLE_TOASTS,
    ADD_CUSTOM_TOAST,
    REMOVE_TOAST,
    REMOVE_TOASTS
} from '@/utils/hooks/context/toast/actions';
import {
    AllNotificationsType,
    ToastsReducerActionsType,
    CustomNotificationType,
    NotificationIdType,
    NotificationOptionsType,
    CustomNotificationDataType
} from '@/utils/hooks/context/toast/types';

// Components
import { ERROR_SECONDARY } from '@/components/base/Button';

type DataContextType = {
    notifications: AllNotificationsType[];
};

const ToastsReducer = (state: DataContextType, action: ToastsReducerActionsType) => {
    switch (action.type) {
        case ADD_TOAST: {
            return {
                ...state,
                notifications: [...state.notifications, action.payload]
            };
        }
        case ADD_CUSTOM_TOAST: {
            return {
                ...state,
                notifications: [
                    ...state.notifications,
                    {
                        ...action.payload,
                        category: 'custom'
                    } as CustomNotificationType
                ]
            };
        }
        case ADD_MULTIPLE_TOASTS: {
            return {
                ...state,
                notifications: [...state.notifications, ...action.payload]
            };
        }
        case REMOVE_TOASTS: {
            return {
                ...state,
                notifications: []
            };
        }
        case REMOVE_TOAST: {
            return {
                ...state,
                notifications: state.notifications.filter(
                    (notification) => notification.id !== action.payload
                )
            };
        }
    }
};

const initialContext: DataContextType = {
    notifications: []
};

type Dispatch = (action: ToastsReducerActionsType) => void;
const ToastsContext = createContext<
    | {
          notificationsInfos: DataContextType;
          toast: (
              data: CustomNotificationDataType,
              options?: NotificationOptionsType
          ) => NotificationIdType;
          toastError: (
              data: CustomNotificationDataType,
              options?: NotificationOptionsType
          ) => NotificationIdType;
          dispatchToasts: Dispatch;
      }
    | undefined
>(undefined);

type ToastsProviderProps = { children: ReactNode };
const ToastsProvider = ({ children }: ToastsProviderProps) => {
    const [state, dispatch] = useReducer(ToastsReducer, initialContext);
    const dispatchToasts = useCallback(
        (args: ToastsReducerActionsType) => dispatch(args),
        [dispatch]
    );
    const notificationsInfos = useMemo(() => state, [state]);

    // Custom toast
    const toast = (
        data: CustomNotificationDataType,
        options: NotificationOptionsType = {}
    ): NotificationIdType => {
        const toastId = uniqid('toast-');
        dispatch({
            type: ADD_CUSTOM_TOAST,
            payload: {
                data,
                options,
                id: toastId
            }
        });

        return toastId;
    };

    // Custom toast error
    const toastError = (
        data: CustomNotificationDataType,
        options: NotificationOptionsType = {}
    ): NotificationIdType => {
        const toastId = uniqid('toast-');
        dispatch({
            type: ADD_CUSTOM_TOAST,
            payload: {
                data: {
                    icon: 'exclamation-point',
                    ...data
                },
                options: {
                    variant: ERROR_SECONDARY,
                    progressBar: {
                        show: true
                    },
                    ...options
                },
                id: toastId
            }
        });

        return toastId;
    };

    return (
        <ToastsContext.Provider value={{ notificationsInfos, toast, toastError, dispatchToasts }}>
            {children}
        </ToastsContext.Provider>
    );
};

const useToastsContext = () => {
    const context = useContext(ToastsContext);
    if (!context) {
        throw new Error('useToastsContext must be used within a ToastsProvider');
    }
    return context;
};
export { ToastsProvider, useToastsContext };
